/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReviewCard from "../Onboarding/ReviewCard";

import reviewImage1 from "../../images/reviewImages/reviewImage1.webp";
import reviewImage2 from "../../images/reviewImages/reviewImage2.webp";
import reviewImage3 from "../../images/reviewImages/reviewImage3.webp";
import reviewImage4 from "../../images/reviewImages/reviewImage4.webp";
import reviewImage5 from "../../images/reviewImages/reviewImage5.webp";

/**
 * ReviewCardsSection (finite)
 * - 1 card on mobile (<768), 3 cards on md+
 * - Slides by ONE card per click with animation
 * - Mobile: no arrows, shows a 3-dot legend
 * - NEW: swipe/drag navigation on mobile
 */
export default function ReviewCardsSection({ reviews, className = "" }) {
  // Default reviews (from your onboarding slide)
  const sample = useMemo(
    () => [
      {
        avatarSrc: reviewImage1,
        name: "Raul M.",
        hashtag: "raulml99",
        rating: 5,
        reviewTitle: "Fantastic",
        reviewText:
          "Just used it and it's fantastic - it recommended me three clinics and honestly I had 2 already under my eye so it's definitely not random results!",
      },
      {
        avatarSrc: reviewImage2,
        name: "Mike R.",
        hashtag: "themikerowl",
        rating: 5,
        reviewTitle: "Good for beginners",
        reviewText:
          "It's a good app, it gives you contacts and information you maybe wouldn't have known about. Definitely good for someone beginning and starting out in the journey of a hair transplant.",
      },
      {
        avatarSrc: reviewImage3,
        name: "Dennis D.",
        hashtag: "dennyd78",
        rating: 5,
        reviewTitle: "Useful, great job",
        reviewText: "Useful website, I think it did a great job. I will use it further.",
      },
      {
        avatarSrc: reviewImage4,
        name: "Burak K.",
        hashtag: "burakko_20",
        rating: 5,
        reviewTitle: "Better than expected!",
        reviewText: "The whole experience was better than I expected. Thanks for everything.",
      },
      {
        avatarSrc: reviewImage5,
        name: "Batu K.",
        hashtag: "bking12",
        rating: 5,
        reviewTitle: "Best Partner Clinics!",
        reviewText: "They have the best clinics partners in Turkey!",
      },
    ],
    []
  );

  const baseData = (reviews && reviews.length ? reviews : sample).slice();
  const dataLen = baseData.length;

  // Responsive (md = 768px)
  const initialIsMobile = typeof window !== "undefined" ? window.innerWidth < 768 : true;
  const [isMobile, setIsMobile] = useState(initialIsMobile);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const cardsPerView = isMobile ? 1 : 3;

  // Current left-most card index (finite)
  const [index, setIndex] = useState(0);

  // Clamp index when viewport or data length changes
  useEffect(() => {
    const maxIndex = Math.max(0, dataLen - cardsPerView);
    setIndex((i) => Math.min(i, maxIndex));
  }, [cardsPerView, dataLen]);

  const maxIndex = Math.max(0, dataLen - cardsPerView);
  const canPrev = index > 0;
  const canNext = index < maxIndex;

  // Animation + swipe state
  const trackRef = useRef(null);
  const wrapRef = useRef(null);
  const isAnimating = useRef(false);
  const [animate, setAnimate] = useState(true);      // toggles CSS transition
  const [dragX, setDragX] = useState(0);             // current drag delta (px)
  const dragStartX = useRef(0);
  const dragging = useRef(false);
  const [wrapW, setWrapW] = useState(0);

  // Recompute wrapper width on resize
  useEffect(() => {
    const measure = () => {
      if (wrapRef.current) setWrapW(wrapRef.current.getBoundingClientRect().width);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const stepPct = 100 / cardsPerView;

  const goPrev = () => {
    if (!canPrev || isAnimating.current) return;
    isAnimating.current = true;
    setAnimate(true);
    setDragX(0);
    setIndex((i) => Math.max(0, i - 1));
  };

  const goNext = () => {
    if (!canNext || isAnimating.current) return;
    isAnimating.current = true;
    setAnimate(true);
    setDragX(0);
    setIndex((i) => Math.min(maxIndex, i + 1));
  };

  const handleTransitionEnd = () => {
    isAnimating.current = false;
  };

  // --- Swipe handlers (pointer + touch fallback) ---
  const startAt = (clientX) => {
    if (!isMobile) return;        // only enable swipe on mobile
    if (isAnimating.current) return;
    dragging.current = true;
    dragStartX.current = clientX;
    setAnimate(false);            // disable transition during drag
  };

  const moveAt = (clientX) => {
    if (!dragging.current) return;
    const dx = clientX - dragStartX.current;

    // apply small resistance at edges
    let damped = dx;
    if ((index === 0 && dx > 0) || (index === maxIndex && dx < 0)) {
      damped = dx * 0.35;
    }
    setDragX(damped);
  };

  const endAt = () => {
    if (!dragging.current) return;
    dragging.current = false;

    const threshold = wrapW * 0.18; // ~18% swipe to trigger
    const dx = dragX;

    // snap to next/prev or back
    if (dx <= -threshold && canNext) {
      goNext();
    } else if (dx >= threshold && canPrev) {
      goPrev();
    } else {
      // not enough swipe → animate back to current index
      setAnimate(true);
      setDragX(0);
      isAnimating.current = true; // we'll get a transitionend for the snap-back
    }
  };

  // Pointer events
  const onPointerDown = (e) => {
    // left button only for mouse; always ok for touch
    if (e.pointerType === "mouse" && e.button !== 0) return;
    startAt(e.clientX);
  };
  const onPointerMove = (e) => moveAt(e.clientX);
  const onPointerUp = () => endAt();
  const onPointerCancel = () => endAt();

  // Touch fallback (older browsers)
  const onTouchStart = (e) => startAt(e.touches[0].clientX);
  const onTouchMove = (e) => moveAt(e.touches[0].clientX);
  const onTouchEnd = () => endAt();
  const onTouchCancel = () => endAt();

  // Compute drag in %
  const dragPct = wrapW ? (dragX / wrapW) * 100 : 0;
  const basePct = index * stepPct;
  const translate = `translate3d(calc(-${basePct}% + ${dragPct}%),0,0)`;

  const arrowBtn =
    "absolute top-1/2 -translate-y-1/2 z-10 h-9 w-9 md:h-10 md:w-10 rounded-full border flex items-center justify-center shadow-sm transition disabled:opacity-30 disabled:cursor-default";
  const arrowStyle = "bg-transparent border-gray-300 text-current hover:bg-gray-100";

  return (
    <section className={`relative select-none pt-6 pb-10 md:pt-8 md:pb-12 bg-transparent mt-8 md:mt-20 ${className}`}>
      <div className="w-11/12 xl:w-5/6 mx-auto">

        {/* Arrows: hidden on mobile, shown on md+ */}
        <button
          type="button"
          onClick={goPrev}
          disabled={!canPrev}
          className={`${arrowBtn} left-2 ${arrowStyle} hidden md:flex`}
          aria-label="Previous reviews"
        >
          <svg viewBox="0 0 20 20" className="w-5 h-5 md:w-6 md:h-6 fill-current" aria-hidden="true">
            <path d="M12.707 3.293a1 1 0 010 1.414L8.414 9H17a1 1 0 110 2H8.414l4.293 4.293a1 1 0 01-1.414 1.414l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 0z" />
          </svg>
        </button>

        <button
          type="button"
          onClick={goNext}
          disabled={!canNext}
          className={`${arrowBtn} right-2 ${arrowStyle} hidden md:flex`}
          aria-label="Next reviews"
        >
          <svg viewBox="0 0 20 20" className="w-5 h-5 md:w-6 md:h-6 fill-current" aria-hidden="true">
            <path d="M7.293 16.707a1 1 0 010-1.414L11.586 11H3a1 1 0 110-2h8.586L7.293 4.707a1 1 0 011.414-1.414l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0z" />
          </svg>
        </button>

        {/* Track-based carousel (animated, finite, swipeable) */}
        <div
          ref={wrapRef}
          className="overflow-hidden touch-pan-y"   // allow vertical page scroll; we handle horizontal
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchCancel}
        >
          <div
            ref={trackRef}
            onTransitionEnd={handleTransitionEnd}
            className={`flex will-change-transform ${animate ? "transition-transform duration-300 ease-out" : ""}`}
            style={{ transform: translate }}
          >
            {baseData.map((rev, i) => (
              <div
                key={i}
                className="px-2 flex-none min-w-0"
                style={{ width: `${100 / cardsPerView}%` }}
              >
                <ReviewCard {...rev} />
              </div>
            ))}
          </div>
        </div>

        {/* Mobile 3-dot legend */}
          <div
            className="mt-4 flex items-center justify-center gap-3"
            aria-label={`Slide ${index + 1} of ${dataLen}`}
          >
            {/* Left dot (prev) */}
            <button
              onClick={goPrev}
              disabled={!canPrev}
              aria-label="Previous review"
              className="disabled:opacity-30"
            >
              <span
                className={`block h-1.5 w-1.5 rounded-full ${
                  canPrev ? "bg-gray-800/70" : "bg-gray-300"
                }`}
              />
            </button>

            {/* Center dot (current) */}
            <span
              className="block h-2.5 w-2.5 rounded-full bg-gray-900"
              aria-hidden="true"
            />

            {/* Right dot (next) */}
            <button
              onClick={goNext}
              disabled={!canNext}
              aria-label="Next review"
              className="disabled:opacity-30"
            >
              <span
                className={`block h-1.5 w-1.5 rounded-full ${
                  canNext ? "bg-gray-800/70" : "bg-gray-300"
                }`}
              />
            </button>
          </div>
      </div>
    </section>
  );
}
